import React, { useState, useEffect, useRef } from 'react';
import { apiFetch } from '../lib/api';

interface ComboTabellataProps {
  label: string;
  value: number | null;
  onChange: (id: number | null) => void;
  societaId: number;
  endpoint: string;
  disabled?: boolean;
}

interface Item {
  id: number;
  nome: string;
}

export default function ComboTabellata({
  label,
  value,
  onChange,
  societaId,
  endpoint,
  disabled = false,
}: ComboTabellataProps) {
  const [items, setItems] = useState<Item[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [errore, setErrore] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedItem = items.find(i => i.id === value) || null;

  // Fetch items quando societaId è valido
  useEffect(() => {
    if (!societaId || societaId <= 0) return;
    setLoadingItems(true);
    apiFetch<{ success: boolean; data: Item[] }>(`${endpoint}?societaId=${societaId}`)
      .then(r => setItems(r.data || []))
      .catch((err) => {
        console.error('[ComboTabellata] fetch fallito:', endpoint, societaId, err);
        setItems([]);
      })
      .finally(() => setLoadingItems(false));
  }, [endpoint, societaId]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
        setErrore('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = items.filter(i =>
    i.nome.toLowerCase().includes(query.toLowerCase())
  );

  const showCrea = query.trim().length > 0 && !items.some(
    i => i.nome.toLowerCase() === query.trim().toLowerCase()
  );

  const handleOpen = () => {
    if (disabled) return;
    setOpen(true);
    setQuery('');
    setErrore('');
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleSelect = (item: Item) => {
    onChange(item.id);
    setOpen(false);
    setQuery('');
  };

  const handleDeselect = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
  };

  const handleCrea = async () => {
    const nome = query.trim();
    if (!nome) return;
    setCreating(true);
    setErrore('');
    try {
      const res = await apiFetch<{ success: boolean; data: Item }>(endpoint, {
        method: 'POST',
        body: JSON.stringify({ nome, societaId }),
      });
      const nuovoItem = res.data;
      setItems(prev => [...prev, nuovoItem].sort((a, b) => a.nome.localeCompare(b.nome)));
      onChange(nuovoItem.id);
      setOpen(false);
      setQuery('');
    } catch (err: any) {
      setErrore(err.message || 'Errore nella creazione');
    } finally {
      setCreating(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { setOpen(false); setQuery(''); }
    if (e.key === 'Enter' && showCrea) { e.preventDefault(); handleCrea(); }
  };

  return (
    <div className="combo-tabellata" ref={wrapperRef}>
      <div
        className={`combo-tabellata__trigger ${disabled ? 'combo-tabellata__trigger--disabled' : ''}`}
        onClick={handleOpen}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleOpen(); }}
      >
        {selectedItem ? (
          <span className="combo-tabellata__badge">
            {selectedItem.nome}
            {!disabled && (
              <button
                type="button"
                className="combo-tabellata__badge-remove"
                onClick={handleDeselect}
                title="Rimuovi"
              >
                ×
              </button>
            )}
          </span>
        ) : loadingItems && value ? (
          <span className="combo-tabellata__placeholder">Caricamento...</span>
        ) : (
          <span className="combo-tabellata__placeholder">Seleziona {label.toLowerCase()}...</span>
        )}
        {!disabled && <span className="combo-tabellata__arrow">▾</span>}
      </div>

      {open && (
        <div className="combo-tabellata__dropdown">
          <div className="combo-tabellata__search">
            <input
              ref={inputRef}
              type="text"
              className="bop-field__input combo-tabellata__input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={`Filtra o crea ${label.toLowerCase()}...`}
              disabled={creating}
            />
          </div>
          <ul className="combo-tabellata__list">
            {filtered.length === 0 && !showCrea && (
              <li className="combo-tabellata__empty">Nessun risultato</li>
            )}
            {filtered.map(item => (
              <li
                key={item.id}
                className={`combo-tabellata__item ${item.id === value ? 'combo-tabellata__item--selected' : ''}`}
                onClick={() => handleSelect(item)}
              >
                {item.nome}
              </li>
            ))}
            {showCrea && (
              <li
                className="combo-tabellata__item combo-tabellata__item--crea"
                onClick={handleCrea}
              >
                {creating ? 'Creazione...' : `Crea «${query.trim()}»`}
              </li>
            )}
          </ul>
          {errore && <div className="combo-tabellata__error">{errore}</div>}
        </div>
      )}
    </div>
  );
}
